import axios from 'axios';
import { baseUrl, httpClient } from '../providers/AppDataProvider';
import { getAuth } from '../providers/AppAuthProvider';

export interface ProxyGeo {
  id: string;
  countryCode: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface ProxyIsp {
  id: string;
  name: string | null;
  asn: number | null;
}

export interface ProxyNode {
  id: string;
  protocol: string;
  host: string;
  port: number;
  username: string | null;
  hasPassword: boolean;
  tier: string;
  geo: ProxyGeo | null;
  isp: ProxyIsp | null;
  source: string | null;
  tags: string[];
  isActive: boolean;
  quarantineLevel: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProxyProvider {
  name: string;
  proxyCount: number;
}

export interface PoolNode {
  id: string;
  name: string;
  description: string | null;
  syncMode: string;
  filterTier: string | null;
  filterProtocol: string | null;
  filterCountryCode: string | null;
  filterIspName: string | null;
  filterIsActive: boolean | null;
  filterTags: string[] | null;
  filterMinScore: number | null;
  filterMaxScore: number | null;
  healthCheckTargets: string[];
  isActive: boolean;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CursorPage<T> {
  nodes: T[];
  nextCursor: string | null;
  totalCount: number;
}

export const PROXY_TIER_CHOICES = [
  { id: 'RESIDENTIAL', name: 'Residential' },
  { id: 'DATACENTER', name: 'Datacenter' },
  { id: 'MOBILE', name: 'Mobile' },
  { id: 'ISP', name: 'ISP' },
] as const;

export const PROXY_PROTOCOL_CHOICES = [
  { id: 'HTTP', name: 'HTTP' },
  { id: 'HTTPS', name: 'HTTPS' },
  { id: 'SOCKS4', name: 'SOCKS4' },
  { id: 'SOCKS5', name: 'SOCKS5' },
] as const;

export const POOL_SYNC_MODE_CHOICES = [
  { id: 'AUTO', name: 'Auto' },
  { id: 'MANUAL', name: 'Manual' },
] as const;

export async function listProxyProviders(): Promise<ProxyProvider[]> {
  const url = `${baseUrl}/proxy-manager/providers`;
  const { json } = await httpClient(url, { method: 'GET' });
  return Array.isArray(json) ? json : json?.data ?? [];
}

export async function listPoolMembers(
  poolId: string,
  params: {
    first?: number;
    after?: string;
    tier?: string;
    protocol?: string;
    countryCode?: string;
    ispName?: string;
    source?: string;
    isActive?: boolean;
    tags?: string;
  } = {}
): Promise<CursorPage<ProxyNode>> {
  const query = new URLSearchParams();
  query.set('first', String(params.first ?? 50));
  if (params.after) query.set('after', params.after);
  if (params.tier) query.set('tier', params.tier);
  if (params.protocol) query.set('protocol', params.protocol);
  if (params.countryCode) query.set('countryCode', params.countryCode);
  if (params.ispName) query.set('ispName', params.ispName);
  if (params.source) query.set('source', params.source);
  if (params.isActive !== undefined) query.set('isActive', String(params.isActive));
  if (params.tags) query.set('tags', params.tags);

  const url = `${baseUrl}/proxy-manager/pools/${poolId}/members?${query.toString()}`;
  const { json } = await httpClient(url, { method: 'GET' });
  return {
    nodes: json?.nodes ?? [],
    nextCursor: json?.nextCursor ?? null,
    totalCount: json?.totalCount ?? 0,
  };
}

export interface ImportProxiesCsvMeta {
  protocol: string;
  source: string;
  tags: string[];
  tier?: string;
}

export async function importProxiesCsv(
  file: File,
  meta: ImportProxiesCsvMeta
): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('protocol', meta.protocol);
  formData.append('source', meta.source);
  (meta.tags ?? []).forEach((tag) => formData.append('tags', tag));
  if (meta.tier) {
    formData.append('tier', meta.tier);
  }

  const { accessToken } = (await getAuth()) || {};
  const headers = {
    Authorization: accessToken ? `Bearer ${accessToken}` : '',
    Accept: 'application/json',
    // Do not set Content-Type manually; axios sets the multipart boundary.
  };

  const url = `${baseUrl}/proxy-manager/proxies/import-csv`;
  const response = await axios.post<any>(url, formData, { headers });
  return response.data;
}

export async function deleteProxiesBatch(ids: string[]): Promise<any> {
  const url = `${baseUrl}/proxy-manager/proxies/delete-batch`;
  const { json } = await httpClient(url, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  return json;
}

export async function syncPool(poolId: string): Promise<any> {
  const url = `${baseUrl}/proxy-manager/pools/${poolId}/sync`;
  const { json } = await httpClient(url, { method: 'POST' });
  return json;
}

export interface ProxyScoreItem {
  proxy: ProxyNode;
  targetKey: string;
  score: number;
}

export async function resetTargetProxyScore(proxyId: string, target: string): Promise<any> {
  const url = `${baseUrl}/proxy-manager/proxies/${proxyId}/reset-target-proxy-score`;
  const { json } = await httpClient(url, {
    method: 'POST',
    body: JSON.stringify({ target }),
  });
  return json;
}

export async function resetProxyQuarantine(proxyId: string): Promise<any> {
  const url = `${baseUrl}/proxy-manager/proxies/${proxyId}/reset-quarantine`;
  const { json } = await httpClient(url, { method: 'POST' });
  return json;
}

export async function testProxyHealthCheck(
  proxyId: string,
  params: { targets: string[]; protocol: string }
): Promise<any> {
  const url = `${baseUrl}/proxy-manager/proxies/${proxyId}/test-health-check`;
  const { json } = await httpClient(url, {
    method: 'POST',
    body: JSON.stringify({ targets: params.targets, protocol: params.protocol }),
  });
  return json;
}

export async function listTargetProxyScores(params: {
  first?: number;
  after?: string;
} = {}): Promise<{ items: ProxyScoreItem[]; nextCursor: string | null }> {
  const query = new URLSearchParams();
  query.set('first', String(params.first ?? 20));
  if (params.after) query.set('after', params.after);
  const url = `${baseUrl}/proxy-manager/target-proxy-scores?${query.toString()}`;
  const { json } = await httpClient(url, { method: 'GET' });
  const items: ProxyScoreItem[] = Array.isArray(json) ? json : (json?.items ?? []);
  return { items, nextCursor: json?.nextCursor ?? null };
}
